import React, { useState, useEffect, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Chip,
  Stack,
  IconButton,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import {
  MyLocation,
  Favorite,
  FavoriteBorder,
  History,
  Home,
  Work,
  School,
  Place,
  ArrowBack,
  LocationOn,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { addRecent, addFavorite, removeFavorite } from '../app/redux/slices/addressBookSlice';
import { themeColors } from '../theme/theme';

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

// Google Maps Autocomplete using Places API (AutocompleteService)
async function fetchAddressSuggestions(query) {
  if (!query || query.trim().length < 2) return [];

  if (!GOOGLE_MAPS_API_KEY) {
    console.error('Google Maps API key is missing!');
    return [];
  }

  // Check if Google Maps API is loaded
  if (typeof window === 'undefined' || !window.google?.maps?.places) {
    // Return empty during SSR or if Maps not loaded yet
    return [];
  }

  try {
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    return new Promise((resolve) => {
      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'IN' },
          types: ['geocode', 'establishment'],
        },
        async (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
            resolve([]);
            return;
          }

          // Get details for each prediction to get coordinates
          const results = await Promise.all(
            predictions.slice(0, 5).map(
              (prediction) =>
                new Promise((resolveSingle) => {
                  placesService.getDetails(
                    { placeId: prediction.place_id },
                    (place, detailStatus) => {
                      if (
                        detailStatus === window.google.maps.places.PlacesServiceStatus.OK &&
                        place
                      ) {
                        resolveSingle({
                          label: prediction.description,
                          address: place.formatted_address || prediction.description,
                          latitude: place.geometry?.location?.lat() || 0,
                          longitude: place.geometry?.location?.lng() || 0,
                          placeId: prediction.place_id,
                        });
                      } else {
                        resolveSingle(null);
                      }
                    }
                  );
                })
            )
          );

          resolve(results.filter(Boolean));
        }
      );
    });
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return [];
  }
}

const RECENT_KEY = 'recent_locations_v1';
const FAV_KEY = 'favorite_locations_v1';

const ADDRESS_TYPES = [
  { label: 'Home', icon: <Home /> },
  { label: 'Office', icon: <Work /> },
  { label: 'College', icon: <School /> },
  { label: 'Other', icon: <Place /> },
];

const AddressSelectorV2 = ({ initialValues = {}, onConfirm, onPrev }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [fields, setFields] = useState({
    door: '',
    street: '',
    area: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    type: 'Home',
    ...initialValues,
  });
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);

  const dispatch = useDispatch();
  const recents = useSelector((state) => state.addressBook.recents);
  const favorites = useSelector((state) => state.addressBook.favorites);

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (typeof window !== 'undefined' && window.google?.maps?.places) {
        setGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMaps()) return;

    // Poll every 100ms for up to 10 seconds
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!googleMapsLoaded) {
        console.error('⚠️ Google Maps API failed to load after 10 seconds');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [googleMapsLoaded]);

  // Check if current address is favorite
  const isFavorite = favorites.some((f) => JSON.stringify(f) === JSON.stringify(fields));

  // Fetch suggestions with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      setAutocompleteOpen(false);
      return;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      const results = await fetchAddressSuggestions(searchQuery);
      setSuggestions(results);
      setAutocompleteOpen(results.length > 0);
      setLoading(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Handle suggestion select
  const handleSuggestionSelect = (event, value) => {
    if (!value) return;
    setSelectedSuggestion(value);
    setSearchQuery(value.label);
    setAutocompleteOpen(false);

    // Parse address from Google Maps data
    const addressParts = value.address?.split(', ') || [];

    setFields((prev) => ({
      ...prev,
      area: addressParts[0] || '',
      city: addressParts[addressParts.length - 3] || '',
      state: addressParts[addressParts.length - 2] || '',
      pincode: '', // Will be extracted if available from address components
      latitude: value.latitude,
      longitude: value.longitude,
    }));
    setSuggestions([]);
  };

  // Current location handler
  const handleCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setCurrentLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        // Check if Google Maps API is loaded
        if (typeof window === 'undefined' || !window.google?.maps) {
          if (typeof window !== 'undefined') {
            console.error('Google Maps API not loaded');
          }
          setCurrentLocationLoading(false);
          return;
        }

        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await geocoder.geocode({
            location: new window.google.maps.LatLng(latitude, longitude),
          });

          if (result.results && result.results.length > 0) {
            const place = result.results[0];
            const addressComponents = place.address_components || [];

            // Extract address components
            const getComponent = (type) => {
              const component = addressComponents.find((c) => c.types.includes(type));
              return component?.long_name || '';
            };

            setSearchQuery(place.formatted_address);
            setFields((prev) => ({
              ...prev,
              door: prev.door || '',
              street: getComponent('route') || getComponent('sublocality_level_2') || '',
              area: getComponent('sublocality_level_1') || getComponent('locality') || '',
              city: getComponent('locality') || getComponent('administrative_area_level_2') || '',
              state: getComponent('administrative_area_level_1') || '',
              pincode: getComponent('postal_code') || '',
              latitude,
              longitude,
              label: place.formatted_address, // Fixed: was incorrectly using 'feature.place_name'
            }));
          }
        } catch (error) {
          console.error('Error fetching location:', error);
        } finally {
          setCurrentLocationLoading(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setCurrentLocationLoading(false);
      }
    );
  };

  // Handle field change
  const handleFieldChange = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  // Autofill from recent/favorite
  const handleAutofill = (item) => {
    setFields(item);
    setSearchQuery(
      [item.door, item.street, item.area, item.city, item.state, item.pincode]
        .filter(Boolean)
        .join(', ')
    );
    dispatch(addRecent(item)); // bump to top
  };

  // Toggle favorite
  const toggleFavorite = () => {
    if (isFavorite) {
      dispatch(removeFavorite(fields));
    } else {
      dispatch(addFavorite(fields));
    }
  };

  // Confirm handler
  const handleConfirm = async () => {
    if (onConfirm) await onConfirm(fields);
    dispatch(addRecent(fields));
  };

  // Validation - either search suggestion selected OR all manual fields filled
  const hasSearchSelection = selectedSuggestion !== null || (fields.latitude && fields.longitude);
  const hasManualFields =
    fields.door && fields.street && fields.area && fields.city && fields.state && fields.pincode;
  const isValid = hasSearchSelection || hasManualFields;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
        py: 4,
        px: 2,
      }}
    >
      <Card
        elevation={8}
        sx={{
          maxWidth: 600,
          mx: 'auto',
          borderRadius: 4,
          overflow: 'visible', // Allow dropdown to overflow
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {onPrev && (
            <IconButton onClick={onPrev} sx={{ color: 'white' }} aria-label="Go back">
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight={700} flex={1}>
            Select Address
          </Typography>
          <IconButton
            onClick={toggleFavorite}
            sx={{ color: 'white' }}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorite ? <Favorite /> : <FavoriteBorder />}
          </IconButton>
        </Box>

        <CardContent sx={{ p: 3, overflow: 'visible' }}>
          {/* Google Maps Loading Alert */}
          {!googleMapsLoaded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading address search... Please wait.
            </Alert>
          )}

          {/* Search Bar */}
          <Autocomplete
            freeSolo
            disablePortal={false}
            open={autocompleteOpen}
            disabled={!googleMapsLoaded}
            onOpen={() => {
              if (suggestions.length > 0) {
                setAutocompleteOpen(true);
              }
            }}
            onClose={(event, reason) => {
              setAutocompleteOpen(false);
            }}
            options={suggestions}
            getOptionLabel={(option) => {
              return typeof option === 'string' ? option : option.label;
            }}
            loading={loading}
            onChange={handleSuggestionSelect}
            inputValue={searchQuery}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchQuery(value);
              }
            }}
            filterOptions={(options) => {
              return options;
            }}
            noOptionsText="No locations found"
            loadingText="Searching locations..."
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={option.label} {...otherProps}>
                  <LocationOn sx={{ mr: 1, color: themeColors.primaryColor }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.text}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.label}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search address, area, or pincode"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: themeColors.primaryColor }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleCurrentLocation}
                          disabled={currentLocationLoading}
                          title="Use current location"
                          aria-label="Use current location"
                        >
                          {currentLocationLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <MyLocation sx={{ color: themeColors.primaryColor }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: themeColors.primaryColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: themeColors.primaryColor,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            )}
            sx={{ mb: 3 }}
            componentsProps={{
              popper: {
                placement: 'bottom-start',
                sx: {
                  zIndex: 9999,
                  position: 'absolute !important',
                },
              },
              paper: {
                sx: {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  mt: 1,
                },
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: '40vh',
                '& .MuiAutocomplete-option': {
                  minHeight: 48,
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                },
              },
            }}
          />

          {/* Debug info */}
          {suggestions.length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Found {suggestions.length} location(s).{' '}
              {autocompleteOpen ? 'Dropdown is OPEN' : 'Dropdown is CLOSED'}
            </Alert>
          )}

          {/* Favorites */}
          {favorites.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Favorite sx={{ fontSize: 18, color: '#e91e63' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Favorites
                </Typography>
              </Stack>
              <Grid container spacing={1}>
                {favorites.map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: themeColors.primaryColor,
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleAutofill(item)}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {ADDRESS_TYPES.find((t) => t.label === item.type)?.icon || <Place />}
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {item.type || 'Address'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.area}, {item.city}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Recents */}
          {recents.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <History sx={{ fontSize: 18, color: themeColors.primaryColor }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Recent
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {recents.slice(0, 3).map((item, i) => (
                  <Card
                    key={i}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: themeColors.primaryColor,
                        transform: 'translateX(4px)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleAutofill(item)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOn sx={{ fontSize: 20, color: 'action.active' }} />
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.door}, {item.street}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.area}, {item.city}, {item.state} - {item.pincode}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Address Details
            </Typography>
          </Divider>

          {/* Form Fields */}
          <Stack spacing={2}>
            <TextField
              label="Door / Flat No. *"
              value={fields.door}
              onChange={(e) => handleFieldChange('door', e.target.value)}
              placeholder="e.g. 12B"
              fullWidth
              size="small"
            />

            <TextField
              label="Street / Building Name *"
              value={fields.street}
              onChange={(e) => handleFieldChange('street', e.target.value)}
              placeholder="e.g. Park Avenue"
              fullWidth
              size="small"
            />

            <TextField
              label="Area / Locality *"
              value={fields.area}
              onChange={(e) => handleFieldChange('area', e.target.value)}
              placeholder="e.g. Banjara Hills"
              fullWidth
              size="small"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City *"
                  value={fields.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  placeholder="e.g. Hyderabad"
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="State *"
                  value={fields.state}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  placeholder="e.g. Telangana"
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            <TextField
              label="Pincode *"
              value={fields.pincode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                handleFieldChange('pincode', val);
              }}
              placeholder="e.g. 500001"
              fullWidth
              size="small"
              inputProps={{ maxLength: 6 }}
            />

            <TextField
              label="Landmark (Optional)"
              value={fields.landmark}
              onChange={(e) => handleFieldChange('landmark', e.target.value)}
              placeholder="e.g. Near Apollo Hospital"
              fullWidth
              size="small"
            />

            {/* Address Type */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Address Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ADDRESS_TYPES.map((type) => (
                  <Chip
                    key={type.label}
                    icon={type.icon}
                    label={type.label}
                    color={fields.type === type.label ? 'primary' : 'default'}
                    onClick={() => handleFieldChange('type', type.label)}
                    sx={{
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        background: themeColors.primaryColor + '22',
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>

          {!isValid && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {hasSearchSelection
                ? 'Please fill remaining required fields marked with *'
                : hasManualFields
                  ? 'Either select from search results OR fill all required fields manually'
                  : 'Please search and select an address OR fill all required fields marked with *'}
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} mt={3}>
            {onPrev && (
              <Button
                variant="outlined"
                onClick={onPrev}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  flex: 1,
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!isValid}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 16,
                py: 1.5,
                borderRadius: 2,
                flex: onPrev ? 2 : 1,
                background: 'linear-gradient(135deg, #000000 0%, #2d2d2d 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                },
              }}
            >
              Continue
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default memo(AddressSelectorV2);
